import FieldNames from '../defaults/FieldNames.json';
import ErrorService from '../services/ErrorService';
import FeatureService from '../services/FeatureService';
import FundingService from '../services/FundingService';
import { removeCommas } from './FormatHelper';
import store from "../redux/store";
// import { getSaveableDate } from './DateHelper';

// Cost fields that get subtracted from the original funding rows when
// amounts are moved to another feature (e.g. Substitute New/Existing Action).
const FUNDING_MOVABLE_COST_FIELDS = [
  FieldNames.fundingFields.personnelCost,
  FieldNames.fundingFields.equipmentCost,
  FieldNames.fundingFields.materialAndSupplyCost,
  FieldNames.fundingFields.procurementCost,
  FieldNames.fundingFields.travelCost,
  FieldNames.fundingFields.grantsFixedCost,
  FieldNames.fundingFields.assetCost,
  FieldNames.fundingFields.contractualCost,
  FieldNames.fundingFields.otherCost,
];

/**
 * Sum up the funding cost fields for a row. When `isPostFire` is true,
 * equipment/material/procurement/travel costs are also included.
 *
 * @param {Record<string,any>} row
 * @param {boolean} [isPostFire]
 * @returns {number}
 */
export function totalFundingAmount(row, isPostFire = false) {

    const FF = FieldNames.fundingFields;
    if (isPostFire) {
        return (
            (row[FF.personnelCost] || 0) +
            (row[FF.equipmentCost] || 0) +
            (row[FF.materialAndSupplyCost] || 0) +
            (row[FF.procurementCost] || 0) +
            (row[FF.travelCost] || 0) +
            (row[FF.grantsFixedCost] || 0) +
            (row[FF.assetCost] || 0) +
            (row[FF.contractualCost] || 0) +
            (row[FF.otherCost] || 0)
        );
    } else {
        return (
            (row[FF.personnelCost] || 0) +
            (row[FF.grantsFixedCost] || 0) +
            (row[FF.assetCost] || 0) +
            (row[FF.contractualCost] || 0) +
            (row[FF.otherCost] || 0)
        );
    }
}

/**
 * Recalculate TotalCost and proportionally reallocate Acres across a set of
 * funding rows using the given calculated area. Mirrors the same funding
 * reallocation logic used by bulk edit (CalculateDefaultFundingAcres), so
 * acreage splits stay consistent app-wide whenever funding costs change
 * across multiple lines - e.g. the Substitute New Action and Substitute
 * Existing Action dialogs.
 *
 * @param {Array<Record<string,any>>} rows
 * @param {number} calculatedArea
 * @param {boolean} [isPostFire]
 * @returns {Array<Record<string,any>>} rows with TotalCost and Acres updated
 */
export function recalcProportionalFundingAcres(rows, calculatedArea, isPostFire = false) {
  const rowsWithTotalCost = (rows || []).map((row) => ({
    ...row,
    [FieldNames.fundingFields.totalCost]: totalFundingAmount(row, isPostFire),
  }));
  return CalculateDefaultFundingAcres(rowsWithTotalCost, calculatedArea);
}

/**
 * Merge a single edited row into the full funding row set, then reallocate
 * Acres proportionally across every row (selected or not) so the split stays
 * consistent - matching how bulk edit reallocates acres whenever a sibling
 * funding row's costs change.
 *
 * @param {Array<Record<string,any>>} rows       - current full set of funding rows (must have `rowId`)
 * @param {Record<string,any>}        newRow      - the row returned from the grid's processRowUpdate
 * @param {number}                    calculatedArea
 * @param {boolean}                   [isPostFire]
 * @returns {Array<Record<string,any>>} the full, recalculated row set
 */
export function mergeFundingRowUpdate(rows, newRow, calculatedArea, isPostFire = false) {
  const mergedRows = (rows || []).map((row) =>
    row.rowId === newRow.rowId ? { ...row, ...newRow } : row
  );
  return recalcProportionalFundingAcres(mergedRows, calculatedArea, isPostFire);
}

/**
 * Clamp every movable cost field on a row to be non-negative (>= 0). Used to
 * sanitize direct user input in the "moved" and "remaining" funding grids so
 * a cost field can never be saved as negative.
 *
 * @param {Record<string,any>} row
 * @returns {Record<string,any>} a shallow copy of the row with cost fields clamped
 */
export function clampNonNegativeCostFields(row) {
  const clamped = { ...row };
  for (const field of FUNDING_MOVABLE_COST_FIELDS) {
    const numericValue = Number(clamped[field]) || 0;
    clamped[field] = numericValue < 0 ? 0 : numericValue;
  }
  return clamped;
}

/**
 * Build the "remaining on the original feature" funding rows, seeded at $0
 * for every movable cost field (and Acres). Used as the starting point for
 * the manual "Remaining Funding" grid shown alongside the "moved" funding
 * grid in the Substitute New/Existing Action dialogs - the user must
 * manually move amounts here (out of the "moved" grid) so the grand total
 * across both grids still matches the original funding total; nothing is
 * redistributed automatically.
 *
 * @param {Array<Record<string,any>>} originalRows - the feature's funding rows before any edits
 * @returns {Array<Record<string,any>>} zeroed-out rows, one per original row
 */
export function buildZeroedRemainingFundingRows(originalRows) {
  return (originalRows || []).map((row) => {
    const zeroedRow = {
      ...row,
      rowId: row[FieldNames.fundingFields.id] ?? row[FieldNames.fundingFields.objectID],
    };
    for (const field of FUNDING_MOVABLE_COST_FIELDS) {
      zeroedRow[field] = 0;
    }
    zeroedRow[FieldNames.fundingFields.totalCost] = 0;
    zeroedRow[FieldNames.fundingAcreageFields.acres] = 0;
    return zeroedRow;
  });
}

export function ResetFeatureFundingLinesByFeature(feature) {
  const userInfo = store.getState().globalContext.userInfo;

  try
  {
    if(!feature){
      throw Error("Missing feature to calculate default funding acreage")
    }

    const featureObj = feature.attributes ?? feature;
    //This will be the JoinID field on anything that isn't a view and ID on anything that is a view
    const joinID = featureObj[FieldNames.featureFields.joinID] ? featureObj[FieldNames.featureFields.joinID] : featureObj[FieldNames.featureFields.id];
    const joinFeature = featureObj[FieldNames.featureFields.joinFeature] ? featureObj[FieldNames.featureFields.joinFeature] : FieldNames.joinFeatureValues.polygon;
    const calculatedArea = featureObj[FieldNames.featureFields.calculatedArea];
    const outFields = "OBJECTID, FundingSourceProgram, TotalCost, Acres";
    return FundingService.getFundingByJoinID(joinID, joinFeature, outFields).then(resp => {
      let fundingRows = [];
      resp.forEach(fundingItem => {
        const obj = {
          [FieldNames.featureFields.objectID]: fundingItem[FieldNames.featureFields.objectID],
          [FieldNames.fundingFields.fundingSourceProgram]: fundingItem[FieldNames.fundingFields.fundingSourceProgram],
          [FieldNames.fundingAcreageFields.acres]: fundingItem[FieldNames.fundingAcreageFields.acres],
          [FieldNames.fundingFields.totalCost]: fundingItem[FieldNames.fundingFields.totalCost],
        }
        fundingRows.push(obj);
      })

      let updatedRows = CalculateDefaultFundingAcres(fundingRows, calculatedArea);
      let relevantUpdates = updatedRows.map(row => {
        let rowObj = {}
        rowObj[FieldNames.featureFields.objectID] = row[FieldNames.featureFields.objectID];
        rowObj[FieldNames.fundingAcreageFields.acres] = row[FieldNames.fundingAcreageFields.acres];
        return rowObj;
      }) 
  
      return FundingService.updateFundingEntries(relevantUpdates).then((response) => {
        if (
          response &&
          response.updateResults &&
          response.updateResults.length > 0 &&
          response.updateResults[0].success
        ) {
          const featureUpdateObj = {
            [FieldNames.featureFields.objectID]: featureObj[FieldNames.featureFields.objectID],
            [FieldNames.featureFields.isFundingAcresManual]: FieldNames.yesNo.no
          }
          featureObj[FieldNames.featureFields.isFundingAcresManual] = FieldNames.yesNo.no;

          FeatureService.updateFeatureAttributes([featureUpdateObj]).then(response => {
            return updatedRows
          }).catch(error => {
            ErrorService.addErrorLogEntry(error);
            return updatedRows;
          });
          return updatedRows;
        } else {
          return [];
        }
      });
    });
  }
  catch(e)
  {
    return new Promise((resolve,reject) => {
      reject(e);
    });
  }
}

export function CalculateDefaultFundingAcres(fundingLines, calculatedArea) {
  if(!fundingLines || fundingLines?.length === 0)
  {
    return [];
  }
  let updatedFundingLines = [];
  const areAllRowsContributed = fundingLines?.length > 0 && 
                                  fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                    === FieldNames.fundingSourcePrograms.contributed).length === fundingLines?.length;

  // Right now this is only Fuels as an alternative, but for future-proofing, keep it as non-contributed
  let nonContributedFundingLines = fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                  !== FieldNames.fundingSourcePrograms.contributed &&
                                    x[FieldNames.fundingFields.totalCost] !== 0);
  let contributedFundingLines = fundingLines.filter(x => x[FieldNames.fundingFields.fundingSourceProgram] 
                                  === FieldNames.fundingSourcePrograms.contributed &&
                                    x[FieldNames.fundingFields.totalCost] !== 0);
  let zeroTotalCostLines = fundingLines.filter(x => x[FieldNames.fundingFields.totalCost] === 0);

  let totalCosts = 0;
  let rowsToUpdate = [];
  let rowsToEmpty = [];

  // Contributed lines should only be counted if all rows are contributed
  if(areAllRowsContributed)
  {
    rowsToUpdate = contributedFundingLines;
    rowsToEmpty = zeroTotalCostLines;
  }
  else
  {
    rowsToUpdate = nonContributedFundingLines;
    rowsToEmpty = zeroTotalCostLines.concat(contributedFundingLines);
  }

  // Calculate split costs for relevant rows
  totalCosts = rowsToUpdate.map(row => row[FieldNames.fundingFields.totalCost]).reduce((a, b) => a + b, 0);
  rowsToUpdate.map(row => {
    const rowCost = row[FieldNames.fundingFields.totalCost];
    if (!rowCost || !totalCosts) {
        return row;
    }
    const calculatedPercentage = rowCost / totalCosts;
    
    row[FieldNames.fundingAcreageFields.acres] = Math.round(calculatedArea * calculatedPercentage);
    return row;
  });

  rowsToEmpty.map(row => {
    row[FieldNames.fundingAcreageFields.acres] = 0;
    return row;
  })

  updatedFundingLines = updatedFundingLines.concat(rowsToUpdate);
  updatedFundingLines = updatedFundingLines.concat(rowsToEmpty);

  return updatedFundingLines;
}

// ---------------------------------------------------------------------------
// Internal helpers – small, pure functions that are individually testable.
// ---------------------------------------------------------------------------

/**
 * Collect unique, non-null values for a given field from all rows.
 *
 * @param {Array<Record<string,any>>} rows
 * @param {string}                    fieldName
 * @returns {string[]}  unique values in insertion order
 */
export function collectUniqueValues(rows, fieldName) {
    const seen = new Set();
    const result = [];
    for (const row of rows) {
        const value = row[fieldName];
        if (value != null && value !== '' && !seen.has(value)) {
            seen.add(value);
            result.push(value);
        }
    }
    return result;
}

/**
 * Join an array into a pipe-delimited string, or return null when empty.
 *
 * @param {string[]} values
 * @returns {string|null}
 */
export function joinPipeDelimited(values) {
    return values.length > 0 ? values.join('|') : null;
}

/**
 * Sum a numeric cost field across all rows.
 * Values are run through `removeCommas` to handle formatted strings.
 *
 * @param {Array<Record<string,any>>} rows
 * @param {string}                    fieldName
 * @returns {number}
 */
export function sumCostField(rows, fieldName) {
    return rows.reduce((total, row) => {
        const rawValue = row[fieldName];
        // Skip null/undefined/empty-string values
        if (rawValue === null || rawValue === undefined || rawValue === '') {
            return total;
        }
        // Normalize formatted strings like "1,000" to a numeric value
        const cleaned = removeCommas(String(rawValue));
        const numeric = Number(cleaned);
        return Number.isFinite(numeric) ? total + numeric : total;
    }, 0);
}

/**
 * Determine whether any row in the set has a BIL flag.
 * A row is BIL when its `IsBIL` field is "Yes" OR its `FundingSourceCategory`
 * contains the substring "bil" (case-insensitive).
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {boolean}
 */
export function hasAnyBilRow(rows) {
    return rows.some(
        (row) =>
            row[FieldNames.fundingFields.isBIL] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bil'))
    );
}

/**
 * Determine whether any row in the set has a BAR flag.
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {boolean}
 */
export function hasAnyBarRow(rows) {
    return rows.some(
        (row) =>
            row[FieldNames.fundingFields.isBAR] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bar'))
    );
}

/**
 * Calculate the total cost of rows that individually qualify as BIL.
 *
 * @param {Array<Record<string,any>>} rows
 * @returns {number}
 */
export function sumBilCost(rows) {
    return rows.reduce((total, row) => {
        const isBil =
            row[FieldNames.fundingFields.isBIL] === FieldNames.yesNo.yes ||
            (row[FieldNames.fundingFields.fundingSourceCategory] &&
                row[FieldNames.fundingFields.fundingSourceCategory].toLowerCase().includes('bil'));
        if (isBil && row[FieldNames.fundingFields.totalCost]) {
            return total + row[FieldNames.fundingFields.totalCost];
        }
        return total;
    }, 0);
}

/**
 * Derive updated WBS ID when the set of funding agencies changes.
 *
 * Rules:
 *  - If BLM was NOT in the old agencies but IS in the new agencies → set WBS to placeholder.
 *  - If BLM WAS in the old agencies but is NOT in the new agencies → strip BLM prefix/suffix.
 *  - Otherwise → leave unchanged.
 *
 * @param {string|null} currentWbsId
 * @param {string|null} oldAgencyString  – pipe-delimited agencies previously on the feature
 * @param {string|null} newAgencyString  – pipe-delimited agencies derived from current rows
 * @returns {string|null|undefined}  the new WBS value, or undefined when no change is needed
 */
export function deriveWbsId(currentWbsId, oldAgencyString, newAgencyString) {
    const hadBlm = oldAgencyString && oldAgencyString.includes(FieldNames.agencies.blm);
    const hasBlm = newAgencyString && newAgencyString.includes(FieldNames.agencies.blm);

    if (!hadBlm && hasBlm) {
        return 'XXXXXXXXXXXX';
    }
    if (hadBlm && !hasBlm && currentWbsId) {
        const prefixRemoved = currentWbsId.replace(FieldNames.wbsPrefixes.blm, '');
        return prefixRemoved.slice(0, -4);
    }
    return undefined; // no change
}

/**
 * Determine default IsRTRL value when agencies change.
 *
 * @param {any}         currentIsRtrl
 * @param {string|null} newAgencyString
 * @returns {string|undefined}  "No" if defaulting is needed, otherwise undefined
 */
export function deriveIsRtrl(currentIsRtrl, newAgencyString) {
    if (
        currentIsRtrl == null &&
        newAgencyString &&
        newAgencyString.includes(FieldNames.agencies.bia)
    ) {
        return FieldNames.yesNo.no;
    }
    return undefined;
}

/** Mapping from funding-row field → unique-values collector key */
const concatenatedFundingFieldMap = [
    { rowField: FieldNames.fundingFields.fundingDepartment,        parentField: FieldNames.featureFields.fundingDepartment },
    { rowField: FieldNames.fundingFields.fundingAgency,            parentField: FieldNames.featureFields.fundingAgency },
    { rowField: FieldNames.fundingFields.fundingRegion,            parentField: FieldNames.featureFields.fundingRegion },
    { rowField: FieldNames.fundingFields.fundingUnit,              parentField: FieldNames.featureFields.fundingUnit },
    { rowField: FieldNames.fundingFields.fundingSubUnit,           parentField: FieldNames.featureFields.fundingSubUnit },
    { rowField: FieldNames.fundingFields.fundingTribe,             parentField: FieldNames.featureFields.fundingTribe },
    { rowField: FieldNames.fundingFields.fundingUnitType,          parentField: FieldNames.featureFields.fundingUnitType },
    { rowField: FieldNames.fundingFields.fundingUnitID,            parentField: FieldNames.featureFields.fundingUnitID },
    { rowField: FieldNames.fundingFields.functionalArea,           parentField: FieldNames.featureFields.functionalArea },
    { rowField: FieldNames.fundingFields.costCenter,               parentField: FieldNames.featureFields.costCenter },
    { rowField: FieldNames.fundingFields.costCode,                 parentField: FieldNames.featureFields.costCode },
    { rowField: FieldNames.fundingFields.fundingSource,            parentField: FieldNames.featureFields.fundingSource },
    { rowField: FieldNames.fundingFields.fundingSourceProgram,     parentField: FieldNames.featureFields.fundingSourceProgram },
    { rowField: FieldNames.fundingFields.fundingSourceCategory,    parentField: FieldNames.featureFields.fundingSourceCategory },
    { rowField: FieldNames.fundingFields.fundingSourceSubCategory, parentField: FieldNames.featureFields.fundingSourceSubCategory },
];

/** Mapping from funding-row cost field → parent estimated-cost field */
const costFieldMap = [
    {
        rowField: FieldNames.fundingFields.assetCost,
        parentField: FieldNames.featureFields.estimatedAssetCost,
    },
    {
        rowField: FieldNames.fundingFields.contractualCost,
        parentField: FieldNames.featureFields.estimatedContractualCost,
    },
    {
        rowField: FieldNames.fundingFields.grantsFixedCost,
        parentField: FieldNames.featureFields.estimatedGrantsFixedCost,
    },
    {
        rowField: FieldNames.fundingFields.personnelCost,
        parentField: FieldNames.featureFields.estimatedPersonnelCost,
    },
    {
        rowField: FieldNames.fundingFields.otherCost,
        parentField: FieldNames.featureFields.estimatedOtherCost,
    },
    {
        rowField: FieldNames.fundingFields.totalCost,
        parentField: FieldNames.featureFields.estimatedTotalCost,
    },

    {
        rowField: FieldNames.fundingFields.equipmentCost,
        parentField: FieldNames.featureFields.estimatedEquipmentCost,
    },
    {
        rowField: FieldNames.fundingFields.materialAndSupplyCost,
        parentField: FieldNames.featureFields.estimatedMaterialAndSupplyCost,
    },
    {
        rowField: FieldNames.fundingFields.procurementCost,
        parentField: FieldNames.featureFields.estimatedProcurementCost,
    },
    {
        rowField: FieldNames.fundingFields.travelCost,
        parentField: FieldNames.featureFields.estimatedTravelCost,
    },
];

// logic mostly from the FundingCostGrid saveFeatureFundingCostValues
export function deriveParentAttributesFromFundingRows(fundingRows, parentAttributes) {
    const userInfo = store.getState().globalContext.userInfo;

    const rows = fundingRows ?? [];
    const attrs = {};

    // concatenated fields using pipe delimiter
    for (const { rowField, parentField } of concatenatedFundingFieldMap) {
        attrs[parentField] = joinPipeDelimited(collectUniqueValues(rows, rowField));
    }

    // cost totals
    for (const { rowField, parentField } of costFieldMap) {
        attrs[parentField] = sumCostField(rows, rowField);
    }

    // BIL / BAR sums
    const isBil = rows.length > 0 && hasAnyBilRow(rows);
    const isBar = rows.length > 0 && hasAnyBarRow(rows);

    attrs[FieldNames.featureFields.isBIL] = isBil ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.isBAR] = isBar ? FieldNames.yesNo.yes : FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.bilFunding] = isBil ? sumBilCost(rows) : 0;

    // wbs id handling
    const oldAgency = parentAttributes?.[FieldNames.featureFields.fundingAgency] ?? null;
    const newAgency = attrs[FieldNames.featureFields.fundingAgency];
    const derivedWbs = deriveWbsId(
        parentAttributes?.[FieldNames.featureFields.wbsID],
        oldAgency,
        newAgency
    );
    if (derivedWbs !== undefined) {
        attrs[FieldNames.featureFields.wbsID] = derivedWbs;
    }

    // BIA isRTRL handling
    const derivedRtrl = deriveIsRtrl(
        parentAttributes?.[FieldNames.featureFields.isRTRL],
        newAgency
    );
    if (derivedRtrl !== undefined) {
        attrs[FieldNames.featureFields.isRTRL] = derivedRtrl;
    }

    attrs[FieldNames.featureFields.isFundingAcresManual] = FieldNames.yesNo.no;
    attrs[FieldNames.featureFields.modifiedBy] =
        userInfo?.[FieldNames.userFields.nifcUsername] ?? null;
    attrs[FieldNames.featureFields.lastModifiedDate] = new Date().getTime();

    return attrs;
}

/**
 * Build a row-by-row comparison between the ORIGINAL funding lines and the
 * "moved" + "remaining" funding lines shown in the Substitute New/Existing
 * Action dialogs. Rather than validating a single grand total across all
 * rows (which allows amounts to be shuffled between unrelated funding
 * lines), each original row must individually balance against its own
 * moved/remaining counterpart (matched by `rowId`) - e.g. original row A's
 * total must equal moved-A + remaining-A, and original row B's total must
 * equal moved-B + remaining-B, independent of each other.
 *
 * @param {Array<Record<string,any>>} initRows       - the original/unedited funding rows (with rowId)
 * @param {Array<Record<string,any>>} movedRows      - the "moved" grid rows (same rowIds as initRows)
 * @param {Array<Record<string,any>>} remainingRows  - the "remaining" grid rows (same rowIds as initRows)
 * @param {{ids: Set<any>}}           selectedRowIds  - selection model for the "moved" grid; only
 *                                                      selected rows actually move, unselected rows
 *                                                      contribute $0 to the moved total
 * @param {boolean}                   [isPostFire]
 * @returns {Array<{rowId: any, row: Record<string,any>, originalTotal: number, movedTotal: number, remainingTotal: number, combinedTotal: number, matches: boolean}>}
 */
export function computeRowLevelFundingTotals(
  initRows,
  movedRows,
  remainingRows,
  selectedRowIds,
  isPostFire = false
) {
  const movedMap = new Map((movedRows || []).map((row) => [row.rowId, row]));
  const remainingMap = new Map((remainingRows || []).map((row) => [row.rowId, row]));
  const selectedSet = new Set(Array.from(selectedRowIds?.ids || []).map(String));

  return (initRows || []).map((row) => {
    const rowId = row.rowId ?? row[FieldNames.fundingFields.id] ?? row[FieldNames.fundingFields.objectID];
    const originalTotal = totalFundingAmount(row, isPostFire);

    const movedRow = movedMap.get(rowId);
    const isSelected = selectedSet.has(String(rowId));
    const movedTotal = isSelected && movedRow ? totalFundingAmount(movedRow, isPostFire) : 0;

    const remainingRow = remainingMap.get(rowId);
    const remainingTotal = remainingRow ? totalFundingAmount(remainingRow, isPostFire) : 0;

    const combinedTotal = movedTotal + remainingTotal;
    const matches = Math.abs(originalTotal - combinedTotal) < 0.01;

    return { rowId, row, originalTotal, movedTotal, remainingTotal, combinedTotal, matches };
  });
}

export function isFundingCellEditable(params, editableFeature, areAllRowsContributed){
  return params?.field === FieldNames.fundingAcreageFields.acres &&
            (params?.row[FieldNames.fundingFields.fundingSourceProgram] !== FieldNames.fundingSourcePrograms.contributed
              || areAllRowsContributed) && (editableFeature != null && editableFeature?.attributes[FieldNames.featureFields.isPoint] !== FieldNames.yesNo.yes && params?.row[FieldNames.fundingFields.joinFeature] === FieldNames.joinFeatureValues.polygon)
}